import { autoDarkColors } from "@/modules/theme/color/darkModeColor.ts";
import type { ThemeProps, ThemeVarGetters, ThemeVariant } from "@/modules/theme/types.ts";
import { Trans } from "@lingui/react/macro";

import png from "@/styles/readyidc/apple-touch-icon.png?url";
import bannerImage from "@/styles/readyidc/banner.png?url";
import icon from "@/styles/readyidc/favicon.ico?url";
import favicon from "@/styles/readyidc/favicon.svg?url";
import isoImage from "@/styles/readyidc/iso27001.jpg?url";
import loginBg from "@/styles/readyidc/login-bg.jpg?url";
import loginIntroBg from "@/styles/readyidc/login-image.png?url";
import logoDark from "@/styles/readyidc/logo-dark.svg?url";
import loginLogo from "@/styles/readyidc/logo.svg";
import logo from "@/styles/readyidc/logo.svg?url";

export function makeTheme(variant: ThemeVariant) {
    const { isDark } = variant;

    const baseColors: ThemeVarGetters = autoDarkColors({
        "color-primary": "#E72529",
        "color-secondary": "#e5e5e5",
    });

    return {
        ...baseColors,

        "logo-image": isDark ? `url("${logoDark}")` : `url("${logo}")`,

        "button-border-radius": "24px",

        "header-logo-width": "108px",
        "header-logo-height": "20px",

        "sidenav-item-text-color": isDark ? "#FFFFFF" : "var(--color-text)",
        "sidenav-item-before-display": "none",
        "sidenav-item-hover-bg-color": "var(--color-primary)",
        "sidenav-item-hover-icon-color": "#FFFFFF",
        "sidenav-item-hover-text-color": "#FFFFFF",
        "sidenav-item-active-bg-color": "var(--color-primary)",
        "sidenav-item-active-icon-color": "#FFFFFF",
        "sidenav-item-active-text-color": "#FFFFFF",
        "sidenav-item-active-before-display": "none",

        //#region Login
        "login-body-bg-color": "var(--color-text)",
        "login-body-bg-image": `url("${loginBg}")`,
        "login-body-bg-size": "cover",
        "login-body-text-color": "var(--color-white)",
        "login-justify-content": "space-between",
        "login-align-items": "stretch",

        "login-intro-display": "flex",
        "login-intro-bg-image": `url("${loginIntroBg}")`,
        "login-intro-bg-size": "45%",
        "login-intro-bg-position": "50% 25%",
        "login-intro-text-align": "left",
        "login-intro-flex-direction": "column",
        "login-intro-justify-content": "flex-end",
        "login-intro-align-items": "flex-start",
        "login-intro-padding": "60px 60px 30px 60px",
        "login-title-font-size": "28px",
        "login-title-text-color": "",
        "login-title-font-weight": 600,
        "login-title-line-height": 1.3,
        "login-title-max-width": "380px",
        "login-title-padding": "0 0 16px 0",
        "login-subtitle-font-size": "16px",
        "login-subtitle-max-width": "420px",

        "login-form-title-font-size": "20px",
        "login-form-bg-color": "var(--color-white)",
        "login-form-text-color": "var(--color-text)",
        "login-form-input-border-color": "#dbe2e9",
        "login-form-button-border-radius": "var(--input-border-radius)",
        "login-form-or-text-color": "#dbe2e9",
        "login-form-or-border-color": "#dbe2e9",
        "login-form-google-bg-color": "#007bf5",
        "login-form-more-text-color": "var(--color-white)",
        "login-form-more-bg-color": "#171b21",
        "login-form-more-border-color": "#171b21",
        //#endregion
    } satisfies ThemeVarGetters;
}

export const props: ThemeProps = {
    favicon,
    icon,
    png,
    loginLogoImg: {
        src: loginLogo,
        style: {
            maxWidth: "240px",
        },
    },
    isoCertificatesHiddenDesktop: true,
    isoCertificates: [
        {
            image: isoImage,
            url: "https://www.bsigroup.com/en-ID/validate-bsi-issued-certificates/client-directory-profile/READYI-0047829035-000",
        },
    ],
    desktopBanner: {
        style: {
            backgroundPosition: "50% 0",
            backgroundSize: "75%",
            backgroundImage: `url("${bannerImage}")`,
        },
        icon: "jp-cluster-icon",
        title: <Trans>Revolutionize your cloud with AMD EPYC</Trans>,
        subtitle: <Trans comment="Availability announcement for AMD EPYC cloud resources">Now available</Trans>,
        url: "/compute/create",
    },
};
